/**
 * Google OAuth callback (`/integrations/google/callback`) — the
 * GOOGLE_OAUTH_REDIRECT_URL target. Google lands here with
 * `?code&state`; the page (which holds the Clerk session) POSTs them to
 * `/v1/integrations/google/exchange` — so the org comes from the
 * session and the HMAC-signed `state` is the CSRF/binding check — then
 * returns to Settings → Integrations. No unauthenticated redirect
 * handler exists server-side by design (see routes/integrations-google.ts).
 */
import { Spinner } from '@/components/ui/spinner';
import { toastError, toastSuccess } from '@/lib/errors';
import { useGoogleExchange } from '@/lib/queries';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef } from 'react';

interface CallbackSearch {
  code?: string;
  state?: string;
  error?: string;
}

export const Route = createFileRoute('/_app/integrations/google/callback')({
  validateSearch: (raw: Record<string, unknown>): CallbackSearch => ({
    ...(typeof raw.code === 'string' ? { code: raw.code } : {}),
    ...(typeof raw.state === 'string' ? { state: raw.state } : {}),
    ...(typeof raw.error === 'string' ? { error: raw.error } : {}),
  }),
  component: GoogleCallbackPage,
});

function GoogleCallbackPage(): ReactElement {
  const { code, state, error } = Route.useSearch();
  const navigate = useNavigate();
  const exchange = useGoogleExchange();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    async function complete(): Promise<void> {
      if (error || !code || !state) {
        toastError(
          'Google connection cancelled',
          error === 'access_denied'
            ? 'You declined the permission request.'
            : 'Missing authorisation details — please try again.',
        );
        await navigate({ to: '/settings/integrations' });
        return;
      }
      try {
        await exchange.mutateAsync({ code, state });
        toastSuccess('Google Search Console connected');
      } catch {
        toastError('Could not complete the Google connection', 'Please try connecting again.');
      }
      await navigate({ to: '/settings/integrations' });
    }
    void complete();
  }, [code, state, error, exchange, navigate]);

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
      <Spinner />
      <p className="text-sm text-[var(--color-mute)]">Connecting Google Search Console…</p>
    </div>
  );
}
